// Definição de todas as permissões disponíveis no sistema
export interface ModulePermissions {
  // Dashboard
  dashboard: {
    view: boolean;
  };
  
  // Pacientes
  patients: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    viewDocuments: boolean;
    uploadDocuments: boolean;
    viewPhotos: boolean;
    uploadPhotos: boolean;
    viewEvolutions: boolean;
    createEvolutions: boolean;
  };
  
  // Agenda
  schedule: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    viewAllAppointments: boolean;
  };
  
  // Orçamentos
  budgets: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    approve: boolean;
    viewAll: boolean;
  };
  
  // Financeiro
  finance: {
    view: boolean;
    viewAnalytics: boolean;
    viewRevenueStats: boolean;
    viewPayroll: boolean;
    viewCommissions: boolean;
    viewReceivables: boolean;
    viewPayables: boolean;
    createTransactions: boolean;
    editTransactions: boolean;
    deleteTransactions: boolean;
    managePayroll: boolean;
    manageCommissions: boolean;
  };
  
  // Configurações
  settings: {
    viewProfile: boolean;
    editProfile: boolean;
    viewOrganization: boolean;
    editOrganization: boolean;
    manageMembers: boolean;
    managePermissions: boolean;
  };

  // Estoque
  stock: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    movements: boolean;
    manageCategories: boolean;
  };

  // Atendimento
  attendance: {
    view: boolean;
    startAppointment: boolean;
  };
}

// Estrutura dos módulos para exibição na interface
export interface PermissionModule {
  id: keyof ModulePermissions;
  name: string;
  icon: string;
  items: PermissionItem[];
}

export interface PermissionItem {
  id: string;
  name: string;
  description: string;
}

export const PERMISSION_MODULES: PermissionModule[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: 'LayoutDashboard',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Ver o painel principal' },
    ],
  },
  {
    id: 'patients',
    name: 'Pacientes',
    icon: 'Users',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Ver lista de pacientes' },
      { id: 'create', name: 'Criar', description: 'Cadastrar novos pacientes' },
      { id: 'edit', name: 'Editar', description: 'Editar dados dos pacientes' },
      { id: 'delete', name: 'Excluir', description: 'Excluir pacientes' },
      { id: 'viewDocuments', name: 'Ver Documentos', description: 'Visualizar documentos dos pacientes' },
      { id: 'uploadDocuments', name: 'Upload Documentos', description: 'Fazer upload de documentos' },
      { id: 'viewPhotos', name: 'Ver Fotos', description: 'Visualizar fotos dos pacientes' },
      { id: 'uploadPhotos', name: 'Upload Fotos', description: 'Fazer upload de fotos' },
      { id: 'viewEvolutions', name: 'Ver Evoluções', description: 'Visualizar evoluções clínicas' },
      { id: 'createEvolutions', name: 'Criar Evoluções', description: 'Registrar evoluções clínicas' },
    ],
  },
  {
    id: 'schedule',
    name: 'Agenda',
    icon: 'Calendar',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Ver agenda de agendamentos' },
      { id: 'create', name: 'Criar', description: 'Criar novos agendamentos' },
      { id: 'edit', name: 'Editar', description: 'Editar agendamentos existentes' },
      { id: 'delete', name: 'Excluir', description: 'Excluir agendamentos' },
      { id: 'viewAllAppointments', name: 'Ver Todos', description: 'Ver agendamentos de todos os profissionais' },
    ],
  },
  {
    id: 'budgets',
    name: 'Orçamentos',
    icon: 'FileText',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Ver orçamentos' },
      { id: 'create', name: 'Criar', description: 'Criar novos orçamentos' },
      { id: 'edit', name: 'Editar', description: 'Editar orçamentos' },
      { id: 'delete', name: 'Excluir', description: 'Excluir orçamentos' },
      { id: 'approve', name: 'Aprovar', description: 'Aprovar orçamentos' },
      { id: 'viewAll', name: 'Ver Todos', description: 'Ver orçamentos de todos os profissionais' },
    ],
  },
  {
    id: 'finance',
    name: 'Financeiro',
    icon: 'DollarSign',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Acessar módulo financeiro' },
      { id: 'viewAnalytics', name: 'Ver Análises', description: 'Ver gráficos e análises financeiras' },
      { id: 'viewRevenueStats', name: 'Ver Faturamento', description: 'Ver receita mensal e lucro líquido' },
      { id: 'viewPayroll', name: 'Ver Folha', description: 'Visualizar folha de pagamento' },
      { id: 'viewCommissions', name: 'Ver Comissões', description: 'Visualizar comissões' },
      { id: 'viewReceivables', name: 'Ver A Receber', description: 'Ver contas a receber' },
      { id: 'viewPayables', name: 'Ver A Pagar', description: 'Ver contas a pagar' },
      { id: 'createTransactions', name: 'Criar Lançamentos', description: 'Criar novos lançamentos' },
      { id: 'editTransactions', name: 'Editar Lançamentos', description: 'Editar lançamentos existentes' },
      { id: 'deleteTransactions', name: 'Excluir Lançamentos', description: 'Excluir lançamentos' },
      { id: 'managePayroll', name: 'Gerenciar Folha', description: 'Criar e editar salários' },
      { id: 'manageCommissions', name: 'Gerenciar Comissões', description: 'Criar e editar comissões' },
    ],
  },
  {
    id: 'settings',
    name: 'Configurações',
    icon: 'Settings',
    items: [
      { id: 'viewProfile', name: 'Ver Perfil', description: 'Visualizar perfil próprio' },
      { id: 'editProfile', name: 'Editar Perfil', description: 'Editar perfil próprio' },
      { id: 'viewOrganization', name: 'Ver Empresa', description: 'Visualizar dados da empresa' },
      { id: 'editOrganization', name: 'Editar Empresa', description: 'Editar dados da empresa' },
      { id: 'manageMembers', name: 'Gerenciar Membros', description: 'Adicionar/remover membros' },
      { id: 'managePermissions', name: 'Gerenciar Permissões', description: 'Configurar permissões de usuários' },
    ],
  },
  {
    id: 'stock',
    name: 'Estoque',
    icon: 'Package',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Ver produtos e movimentações' },
      { id: 'create', name: 'Criar', description: 'Cadastrar novos produtos' },
      { id: 'edit', name: 'Editar', description: 'Editar produtos existentes' },
      { id: 'delete', name: 'Excluir', description: 'Excluir produtos' },
      { id: 'movements', name: 'Movimentações', description: 'Registrar entradas e saídas' },
      { id: 'manageCategories', name: 'Gerenciar Categorias', description: 'Criar e editar categorias' },
    ],
  },
  {
    id: 'attendance',
    name: 'Atendimento',
    icon: 'Stethoscope',
    items: [
      { id: 'view', name: 'Visualizar', description: 'Acessar tela de atendimento' },
      { id: 'startAppointment', name: 'Iniciar Atendimento', description: 'Iniciar e gerenciar atendimentos' },
    ],
  },
];

// Templates de permissões por role
export const PERMISSION_TEMPLATES: Record<string, Partial<ModulePermissions>> = {
  admin: {
    dashboard: { view: true },
    patients: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      viewDocuments: true,
      uploadDocuments: true,
      viewPhotos: true,
      uploadPhotos: true,
      viewEvolutions: true,
      createEvolutions: true,
    },
    schedule: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      viewAllAppointments: true,
    },
    budgets: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      approve: true,
      viewAll: true,
    },
    finance: {
      view: true,
      viewAnalytics: true,
      viewRevenueStats: true,
      viewPayroll: true,
      viewCommissions: true,
      viewReceivables: true,
      viewPayables: true,
      createTransactions: true,
      editTransactions: true,
      deleteTransactions: true,
      managePayroll: true,
      manageCommissions: true,
    },
    settings: {
      viewProfile: true,
      editProfile: true,
      viewOrganization: true,
      editOrganization: true,
      manageMembers: true,
      managePermissions: true,
    },
    stock: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      movements: true,
      manageCategories: true,
    },
    attendance: {
      view: true,
      startAppointment: true,
    },
  },
  secretary: {
    dashboard: { view: true },
    patients: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      viewDocuments: true,
      uploadDocuments: true,
      viewPhotos: true,
      uploadPhotos: true,
      viewEvolutions: true,
      createEvolutions: false,
    },
    schedule: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      viewAllAppointments: true,
    },
    budgets: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      approve: false,
      viewAll: true,
    },
    finance: {
      view: true,
      viewAnalytics: false,
      viewRevenueStats: false,
      viewPayroll: false,
      viewCommissions: false,
      viewReceivables: true,
      viewPayables: true,
      createTransactions: true,
      editTransactions: true,
      deleteTransactions: false,
      managePayroll: false,
      manageCommissions: false,
    },
    settings: {
      viewProfile: true,
      editProfile: true,
      viewOrganization: false,
      editOrganization: false,
      manageMembers: false,
      managePermissions: false,
    },
    stock: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      movements: true,
      manageCategories: false,
    },
    attendance: {
      view: false,
      startAppointment: false,
    },
  },
  doctor: {
    dashboard: { view: true },
    patients: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      viewDocuments: true,
      uploadDocuments: true,
      viewPhotos: true,
      uploadPhotos: true,
      viewEvolutions: true,
      createEvolutions: true,
    },
    schedule: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      viewAllAppointments: false,
    },
    budgets: {
      view: true,
      create: true,
      edit: true,
      delete: false,
      approve: true,
      viewAll: false,
    },
    finance: {
      view: false,
      viewAnalytics: false,
      viewRevenueStats: false,
      viewPayroll: false,
      viewCommissions: false,
      viewReceivables: false,
      viewPayables: false,
      createTransactions: false,
      editTransactions: false,
      deleteTransactions: false,
      managePayroll: false,
      manageCommissions: false,
    },
    settings: {
      viewProfile: true,
      editProfile: true,
      viewOrganization: false,
      editOrganization: false,
      manageMembers: false,
      managePermissions: false,
    },
    stock: {
      view: true,
      create: false,
      edit: false,
      delete: false,
      movements: false,
      manageCategories: false,
    },
    attendance: {
      view: true,
      startAppointment: true,
    },
  },
};
